import { useState, useEffect, useRef } from 'react';
import QRCode from 'qrcode';
import { DataTable, Button, Modal } from '@sundkortid/ui';
import { api } from '../../api/client';
import { AdminNav } from './AdminDashboardPage';

interface Pool {
  id: string;
  name: string;
  slug: string;
  address: string | null;
  city: string;
  isActive: boolean;
  createdAt: string;
}

interface QrTarget {
  poolId: string;
  poolName: string;
  slug: string;
}

const EMPTY_FORM = { name: '', address: '', city: 'Reykjavík' };

function QrModal({ target, onClose }: { target: QrTarget; onClose: () => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    api
      .get<{ payload: string; name: string; slug: string; isNew: boolean }>(
        `/pools/${target.poolId}/qr`,
      )
      .then(async (data) => {
        const url = await QRCode.toDataURL(data.payload, {
          errorCorrectionLevel: 'Q',
          width: 512,
          margin: 2,
        });
        setDataUrl(url);
      })
      .catch(() => setError('Tókst ekki að sækja QR kóða'))
      .finally(() => setLoading(false));
  }, [target.poolId]);

  const handleDownload = () => {
    if (!dataUrl) return;
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `${target.slug}-qr.png`;
    a.click();
  };

  const handlePrint = () => {
    if (!dataUrl) return;
    const win = window.open('', '_blank');
    if (!win) return;

    const heading = win.document.createElement('h2');
    heading.textContent = target.poolName;
    heading.style.cssText = 'font-family:sans-serif;text-align:center;margin-bottom:16px';

    const img = win.document.createElement('img');
    img.src = dataUrl;
    img.style.cssText = 'display:block;max-width:400px;margin:0 auto';
    img.onload = () => win.print();

    win.document.body.style.cssText = 'padding:40px;text-align:center';
    win.document.body.appendChild(heading);
    win.document.body.appendChild(img);
  };

  return (
    <Modal open onClose={onClose} title={`QR kóði — ${target.poolName}`}>
      <div className="flex flex-col items-center gap-4">
        {loading && (
          <div className="w-48 h-48 bg-gray-100 rounded-xl animate-pulse" />
        )}
        {error && (
          <p className="text-sm text-red-600 bg-red-50 rounded-lg px-4 py-3 w-full text-center">
            {error}
          </p>
        )}
        {dataUrl && !loading && (
          <>
            <img src={dataUrl} alt="QR kóði" className="w-56 h-56 rounded-xl border border-gray-100" />
            <p className="text-xs text-gray-400 text-center">
              Prentaðu og festu við inngang sundlaugarinnar.
            </p>
          </>
        )}
        <canvas ref={canvasRef} className="hidden" />
      </div>
      <div className="flex gap-2 justify-end mt-4">
        <Button variant="outline" onClick={onClose}>Loka</Button>
        <Button variant="outline" disabled={!dataUrl} onClick={handlePrint}>Prenta</Button>
        <Button disabled={!dataUrl} onClick={handleDownload}>Hlaða niður</Button>
      </div>
    </Modal>
  );
}

export function AdminPoolsPage() {
  const [pools, setPools] = useState<Pool[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editTarget, setEditTarget] = useState<Pool | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Pool | null>(null);
  const [qrTarget, setQrTarget] = useState<QrTarget | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchPools = async () => {
    setLoading(true);
    try {
      const data = await api.get<Pool[]>('/pools');
      setPools(data);
    } catch {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPools(); }, []);

  const handleCreate = async () => {
    setActionLoading(true);
    try {
      await api.post('/pools', form);
      setShowCreate(false);
      setForm(EMPTY_FORM);
      fetchPools();
    } catch {
    } finally {
      setActionLoading(false);
    }
  };

  const handleEdit = async () => {
    if (!editTarget) return;
    setActionLoading(true);
    try {
      await api.patch(`/pools/${editTarget.id}`, {
        name: form.name,
        address: form.address || undefined,
        city: form.city,
      });
      setEditTarget(null);
      fetchPools();
    } catch {
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setActionLoading(true);
    try {
      await api.delete(`/pools/${deleteTarget.id}`);
      setDeleteTarget(null);
      fetchPools();
    } catch {
    } finally {
      setActionLoading(false);
    }
  };

  const openEdit = (pool: Pool) => {
    setForm({ name: pool.name, address: pool.address ?? '', city: pool.city });
    setEditTarget(pool);
  };

  const columns = [
    { key: 'name', header: 'Nafn' },
    { key: 'city', header: 'Borg' },
    { key: 'address', header: 'Heimilisfang', render: (row: Pool) => row.address ?? '—' },
    {
      key: 'isActive',
      header: 'Virk',
      render: (row: Pool) => (
        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${row.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
          {row.isActive ? 'Já' : 'Nei'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Aðgerðir',
      render: (row: Pool) => (
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setQrTarget({ poolId: row.id, poolName: row.name, slug: row.slug })}>QR</Button>
          <Button size="sm" variant="outline" onClick={() => openEdit(row)}>Breyta</Button>
          <Button size="sm" variant="danger" onClick={() => setDeleteTarget(row)}>Eyða</Button>
        </div>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-100 px-6 py-4">
        <div className="max-w-6xl mx-auto flex flex-col gap-4">
          <h1 className="text-xl font-title font-bold text-primary">Sundkortið — Stjórnborð</h1>
          <AdminNav />
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-title font-bold text-primary">Sundlaugar</h2>
          <Button onClick={() => { setForm(EMPTY_FORM); setShowCreate(true); }}>Ný sundlaug</Button>
        </div>

        {loading ? (
          <p className="text-gray-500">Hleð gögnum...</p>
        ) : (
          <DataTable columns={columns} data={pools} keyExtractor={(row) => row.id} />
        )}
      </main>

      {qrTarget && <QrModal target={qrTarget} onClose={() => setQrTarget(null)} />}

      {/* Create Modal */}
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Ný sundlaug">
        <div className="space-y-3">
          <input placeholder="Nafn" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full" />
          <input placeholder="Heimilisfang" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full" />
          <input placeholder="Borg" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full" />
        </div>
        <div className="flex gap-2 justify-end mt-4">
          <Button variant="outline" onClick={() => setShowCreate(false)}>Hætta við</Button>
          <Button loading={actionLoading} onClick={handleCreate}>Búa til</Button>
        </div>
      </Modal>

      {/* Edit Modal */}
      <Modal open={!!editTarget} onClose={() => setEditTarget(null)} title="Breyta sundlaug">
        <div className="space-y-3">
          <input placeholder="Nafn" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full" />
          <input placeholder="Heimilisfang" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full" />
          <input placeholder="Borg" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full" />
        </div>
        <div className="flex gap-2 justify-end mt-4">
          <Button variant="outline" onClick={() => setEditTarget(null)}>Hætta við</Button>
          <Button loading={actionLoading} onClick={handleEdit}>Vista</Button>
        </div>
      </Modal>

      {/* Delete Modal */}
      <Modal open={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Eyða sundlaug">
        <p className="text-sm text-gray-600 mb-4">
          Ertu viss um að þú viljir eyða <strong>{deleteTarget?.name}</strong>?
        </p>
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={() => setDeleteTarget(null)}>Hætta við</Button>
          <Button variant="danger" loading={actionLoading} onClick={handleDelete}>Eyða</Button>
        </div>
      </Modal>
    </div>
  );
}
